import { useEffect } from 'react';
import api from '@/soluqr/api/client';
import { withCustomerSession } from '@/lib/customerSession';
import { toast } from 'sonner';
import { readLocalCustomer, isLocalCustomerComplete } from '@/lib/localCustomer';
import { normalizeCustomerPhone } from '@/lib/phone';
import { readLastOrderForBusiness } from '@/lib/localLastOrder';
import { buildWelcomeSaludoProducts } from '@/lib/assistantWelcomeProducts';
import { buildAssistantWelcomeMessage } from '@/lib/assistant/assistantWelcomeMessage';
import { getOrderToRepeat } from '@/lib/assistant/assistantRepeatOrder';
import { logStructured } from '@/lib/networkLog';
import { loadPublicMenuPayload } from '@/lib/menuFallback';
import {
  fetchPublicAssistantTraining,
  mergeAssistantTrainingIntoMenuPayload,
} from '@/lib/menuAssistantTraining';
import { isDemoBusinessId, getDemoMenuPayload } from '@/lib/demo/demoBusiness';
import { trackEvent } from '@/lib/analytics/trackEvent';
import { getEffectiveBusinessId } from '@/lib/tenant/getEffectiveBusinessId';

/** Carga menú, cliente, último pedido y mensaje de bienvenida. */
export function useAssistantChatLoad({
  routeBid,
  businessId,
  businessIdReady,
  menuReloadKey,
  lastOrder,
  navigate,
  handleMainChoiceRef,
  demoAssistantUsedRef,
  setBusinessId,
  setBusinessIdReady,
  setBusiness,
  setMenuData,
  setLoadError,
  setLoading,
  setLastOrder,
  setBenefitsProgressSnapshot,
  setMessages,
  setCurrentStep,
  setRepeatOrderAvailable,
  setCustomerId,
}) {
  useEffect(() => {
    if (routeBid) {
      setBusinessId(routeBid);
      setBusinessIdReady(true);
      return;
    }
    let cancelled = false;
    (async () => {
      const id = await getEffectiveBusinessId('');
      if (!cancelled) {
        setBusinessId(id);
        setBusinessIdReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [routeBid, setBusinessId, setBusinessIdReady]);

  useEffect(() => {
    demoAssistantUsedRef.current = false;
  }, [businessId, demoAssistantUsedRef]);

  useEffect(() => {
    if (!businessIdReady) return;
    if (!businessId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLastOrder(null);
    setLoadError(false);
    setLoading(true);

    (async () => {
      try {
        const loaded = await loadPublicMenuPayload(api, businessId, {
          isDemo: isDemoBusinessId(businessId),
          getDemoPayload: getDemoMenuPayload,
        });
        if (cancelled) return;
        if (loaded.loadError || !loaded.payload) {
          setMenuData(null);
          setLoadError(true);
          if (loaded.toastMessage) toast.error(loaded.toastMessage);
          return;
        }
        let menuPayload = loaded.payload;
        if (!isDemoBusinessId(businessId)) {
          const training = await fetchPublicAssistantTraining(api, businessId);
          if (cancelled) return;
          menuPayload = mergeAssistantTrainingIntoMenuPayload(menuPayload, training);
        }
        if (loaded.toastMessage && loaded.fromFallback) {
          toast.message(loaded.toastMessage);
        }
        setBusiness(menuPayload.business || {});
        setMenuData(menuPayload);

        const lc = readLocalCustomer(businessId);
        const profile = isLocalCustomerComplete(lc) ? lc : null;
        const firstName = profile?.name ? profile.name.trim().split(/\s+/)[0] : '';
        let clubLine = '';
        let bpSnapshot = null;
        if (!isDemoBusinessId(businessId)) {
          try {
            const phone = normalizeCustomerPhone(profile?.phone || '');
            if (phone) {
              const { data: bp } = await api.get(
                '/customers/benefits-progress',
                withCustomerSession({ params: { business_id: businessId, phone } }, businessId),
              );
              if (cancelled) return;
              bpSnapshot = bp;
              if (bp?.enabled) {
                setBenefitsProgressSnapshot(bp);
                const ar = Math.max(0, Number(bp.available_rewards) || 0);
                const premio = bp.reward_text ? `: ${bp.reward_text}` : '';
                if (ar > 0) {
                  clubLine =
                    ar === 1
                      ? `\n\n🎁 Tienes 1 premio listo${premio}.`
                      : `\n\n🎁 Tienes ${ar} premios listos${premio}.`;
                } else {
                  clubLine = `\n\n⭐ Vas ${bp.total_orders} compras: faltan ${bp.remaining_orders} para el próximo premio${bp.reward_text ? ` (${bp.reward_text})` : ''}.`;
                }
              } else {
                setBenefitsProgressSnapshot(null);
              }
            } else {
              setBenefitsProgressSnapshot(null);
            }
          } catch (e) {
            console.error('Assistant benefits-progress', e);
            if (!cancelled) setBenefitsProgressSnapshot(null);
          }
        } else {
          setBenefitsProgressSnapshot(null);
        }

        if (cancelled) return;

        let apiLastOrder = null;
        if (!isDemoBusinessId(businessId)) {
          const storedCustomerId = localStorage.getItem(`soluqr_customer_${businessId}`);
          if (storedCustomerId) {
            setCustomerId(storedCustomerId);
            try {
              const { data } = await api.get(
                `/menu/${businessId}/customer/${storedCustomerId}/last-order`,
                withCustomerSession({}, businessId)
              );
              if (cancelled) return;
              if (data?.items?.length > 0) {
                apiLastOrder = data;
                setLastOrder(data);
              }
            } catch {
              if (!cancelled) console.log('No previous order found');
            }
          }
        }

        if (cancelled) return;

        const { products: welcomeHighlightProducts, includedDailyPromo } = buildWelcomeSaludoProducts(
          menuPayload,
          bpSnapshot
        );
        const dailyPromoLine = includedDailyPromo ? '\n\n⭐ Promo del día' : '';

        const orderToRepeat = getOrderToRepeat(businessId, apiLastOrder);
        const { welcomeText, welcomeOptions } = buildAssistantWelcomeMessage({
          businessId,
          firstName,
          clubLine,
          dailyPromoLine,
          orderToRepeat,
          navigate,
          routeBid,
          onKnowWhatToOrder: () => handleMainChoiceRef.current?.('know-order'),
          onBrowseMenu: () => handleMainChoiceRef.current?.('browse-menu'),
          onStartRepeat: (order) => handleMainChoiceRef.current?.('previous', order),
        });

        setMessages([
          {
            type: 'bot',
            text: welcomeText,
            options: welcomeOptions,
            products: welcomeHighlightProducts.length ? welcomeHighlightProducts : undefined,
            timestamp: new Date(),
          },
        ]);
        setCurrentStep('main');
        if (!isDemoBusinessId(businessId)) {
          trackEvent('assistant_opened', { business_id: businessId });
        }
      } catch (error) {
        console.error('Error loading menu:', error);
        if (!cancelled) {
          logStructured('menu', {
            event: 'assistant_load_failed',
            businessId,
            status: error.response?.status,
            code: error.code,
          });
          setMenuData(null);
          toast.error(
            error?.response?.status === 403
              ? 'Este negocio no está disponible actualmente'
              : 'Error al cargar el menú'
          );
          setLoadError(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [businessId, businessIdReady, menuReloadKey, setBusiness]);

  useEffect(() => {
    if (!businessIdReady || !businessId) return;
    if (isDemoBusinessId(businessId)) return;
    let cancelled = false;

    const loadCustomerData = async () => {
      const storedCustomerId = localStorage.getItem(`soluqr_customer_${businessId}`);
      if (storedCustomerId) {
        setCustomerId(storedCustomerId);
        try {
          const response = await api.get(
            `/menu/${businessId}/customer/${storedCustomerId}/last-order`,
            withCustomerSession({}, businessId)
          );
          if (cancelled) return;
          if (response.data?.items?.length > 0) {
            setLastOrder(response.data);
          }
        } catch {
          if (!cancelled) console.log('No previous order found');
        }
      }
    };

    loadCustomerData();
    return () => {
      cancelled = true;
    };
  }, [businessId, businessIdReady, setCustomerId, setLastOrder]);

  useEffect(() => {
    if (!businessIdReady || !businessId) return;
    const localOk = !!readLastOrderForBusiness(businessId)?.items?.length;
    const apiOk = !!lastOrder?.items?.length;
    setRepeatOrderAvailable(localOk || apiOk);
  }, [businessId, businessIdReady, lastOrder, setRepeatOrderAvailable]);

  /** Sincroniza botón de repetir cuando llega el pedido por API (misma fuente que el resumen). */
  useEffect(() => {
    if (!businessIdReady || !businessId) return;
    setMessages((prev) => {
      if (prev.length < 1 || prev[0]?.type !== 'bot') return prev;
      const orderToRepeat = getOrderToRepeat(businessId, lastOrder);
      const { welcomeOptions } = buildAssistantWelcomeMessage({
        businessId,
        orderToRepeat,
        navigate,
        routeBid,
        onKnowWhatToOrder: () => handleMainChoiceRef.current?.('know-order'),
        onBrowseMenu: () => handleMainChoiceRef.current?.('browse-menu'),
        onStartRepeat: (order) => handleMainChoiceRef.current?.('previous', order),
      });
      let welcomeText = prev[0].text || '';
      const repeatHint = '\n\n📦 Tenés un pedido anterior guardado.';
      const hasRepeat = Boolean(orderToRepeat?.items?.length);
      const hasHint = welcomeText.includes('pedido anterior guardado');
      if (hasRepeat && !hasHint) {
        welcomeText += repeatHint;
      } else if (!hasRepeat && hasHint) {
        welcomeText = welcomeText.replace(/\n\n📦 Tenés un pedido anterior guardado\./, '');
      }
      return [{ ...prev[0], text: welcomeText, options: welcomeOptions }];
    });
  }, [
    lastOrder,
    businessId,
    businessIdReady,
    navigate,
    routeBid,
    handleMainChoiceRef,
    setMessages,
  ]);
}
